<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SoftphoneExtensionsAPI from 'dashboard/api/softphoneExtensions';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

// The box reports registrations every 20s; refresh a bit faster than that.
const REFRESH_MS = 10000;

const { t, locale } = useI18n();

const rows = ref([]);
const isFetching = ref(true);
const busyUserId = ref(null);
const pending = ref(null); // { action: 'remove' | 'password', row }
const confirmDialog = ref(null);
let refreshTimer = null;

const fetchRows = async () => {
  try {
    const { data } = await SoftphoneExtensionsAPI.get();
    rows.value = data;
  } catch (error) {
    useAlert(t('SOFTPHONE_SETTINGS.ERRORS.FETCH'));
  } finally {
    isFetching.value = false;
  }
};

const replaceRow = row => {
  rows.value = rows.value.map(r => (r.user_id === row.user_id ? row : r));
};

const counts = computed(() => {
  const withExtension = rows.value.filter(r => r.extension);
  return {
    online: withExtension.filter(r => r.online).length,
    enabled: withExtension.filter(r => r.enabled).length,
    total: withExtension.length,
  };
});

const statusOf = row => {
  if (!row.extension) return 'none';
  if (!row.enabled) return 'disabled';
  return row.online ? 'online' : 'offline';
};

const STATUS_DOT = {
  online: 'bg-n-teal-9',
  offline: 'bg-n-amber-9',
  disabled: 'bg-n-slate-8',
  none: 'bg-n-slate-6',
};

const intlLocale = computed(() => (locale.value || 'en').replace('_', '-'));

const lastSeenLabel = row => {
  if (!row.last_seen_at) return t('SOFTPHONE_SETTINGS.STATUS.NEVER');
  const seen = new Date(row.last_seen_at);
  const sameDay = seen.toDateString() === new Date().toDateString();
  const formatted = sameDay
    ? seen.toLocaleTimeString(intlLocale.value, {
        hour: '2-digit',
        minute: '2-digit',
      })
    : seen.toLocaleDateString(intlLocale.value, {
        day: '2-digit',
        month: '2-digit',
      });
  return t('SOFTPHONE_SETTINGS.STATUS.LAST_SEEN', { time: formatted });
};

const runFor = async (row, request, successKey) => {
  busyUserId.value = row.user_id;
  try {
    const { data } = await request();
    if (data) replaceRow(data);
    if (successKey) useAlert(t(successKey));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('SOFTPHONE_SETTINGS.ERRORS.GENERIC')
    );
  } finally {
    busyUserId.value = null;
  }
};

const activate = row =>
  runFor(
    row,
    () => SoftphoneExtensionsAPI.create({ user_id: row.user_id }),
    'SOFTPHONE_SETTINGS.SUCCESS.ACTIVATED'
  );

const toggleEnabled = (row, enabled) =>
  runFor(
    row,
    () => SoftphoneExtensionsAPI.update(row.credential_id, { enabled }),
    enabled
      ? 'SOFTPHONE_SETTINGS.SUCCESS.ENABLED'
      : 'SOFTPHONE_SETTINGS.SUCCESS.DISABLED'
  );

const askConfirm = (action, row) => {
  pending.value = { action, row };
  confirmDialog.value?.open();
};

const confirmPending = async () => {
  const { action, row } = pending.value;
  confirmDialog.value?.close();
  if (action === 'password') {
    await runFor(
      row,
      () => SoftphoneExtensionsAPI.regeneratePassword(row.credential_id),
      'SOFTPHONE_SETTINGS.SUCCESS.PASSWORD'
    );
    return;
  }
  await runFor(
    row,
    async () => {
      await SoftphoneExtensionsAPI.delete(row.credential_id);
      replaceRow({
        ...row,
        credential_id: null,
        extension: null,
        enabled: null,
        online: false,
        last_seen_at: null,
      });
      return {};
    },
    'SOFTPHONE_SETTINGS.SUCCESS.REMOVED'
  );
};

const confirmTitle = computed(() =>
  pending.value?.action === 'password'
    ? t('SOFTPHONE_SETTINGS.CONFIRM.PASSWORD_TITLE', {
        name: pending.value.row.name,
      })
    : t('SOFTPHONE_SETTINGS.CONFIRM.REMOVE_TITLE', {
        name: pending.value?.row.name || '',
      })
);

const confirmDescription = computed(() =>
  pending.value?.action === 'password'
    ? t('SOFTPHONE_SETTINGS.CONFIRM.PASSWORD_DESCRIPTION')
    : t('SOFTPHONE_SETTINGS.CONFIRM.REMOVE_DESCRIPTION', {
        extension: pending.value?.row.extension || '',
      })
);

onMounted(() => {
  fetchRows();
  refreshTimer = setInterval(fetchRows, REFRESH_MS);
});

onBeforeUnmount(() => clearInterval(refreshTimer));
</script>

<template>
  <SettingsLayout
    :is-loading="isFetching"
    :no-records-found="!rows.length"
    :no-records-message="t('SOFTPHONE_SETTINGS.NO_AGENTS')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('SOFTPHONE_SETTINGS.TITLE')"
        :description="t('SOFTPHONE_SETTINGS.DESCRIPTION')"
      />
    </template>

    <template #body>
      <div class="flex flex-wrap gap-2 mb-4">
        <span
          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-n-alpha-2 text-sm text-n-slate-12"
        >
          <span class="rounded-full size-2 bg-n-teal-9" />
          {{ t('SOFTPHONE_SETTINGS.SUMMARY.ONLINE', { n: counts.online }) }}
        </span>
        <span
          class="inline-flex items-center px-2.5 py-1 rounded-full bg-n-alpha-2 text-sm text-n-slate-11"
        >
          {{ t('SOFTPHONE_SETTINGS.SUMMARY.ENABLED', { n: counts.enabled }) }}
        </span>
        <span
          class="inline-flex items-center px-2.5 py-1 rounded-full bg-n-alpha-2 text-sm text-n-slate-11"
        >
          {{ t('SOFTPHONE_SETTINGS.SUMMARY.TOTAL', { n: counts.total }) }}
        </span>
      </div>

      <div class="overflow-x-auto rounded-lg border border-n-weak">
        <table class="w-full min-w-[40rem]">
          <thead class="bg-n-alpha-black2">
            <tr>
              <th
                class="px-4 py-3 text-start text-xs font-medium text-n-slate-11 uppercase"
              >
                {{ t('SOFTPHONE_SETTINGS.LIST.AGENT') }}
              </th>
              <th
                class="px-4 py-3 text-start text-xs font-medium text-n-slate-11 uppercase"
              >
                {{ t('SOFTPHONE_SETTINGS.LIST.EXTENSION') }}
              </th>
              <th
                class="px-4 py-3 text-start text-xs font-medium text-n-slate-11 uppercase"
              >
                {{ t('SOFTPHONE_SETTINGS.LIST.STATUS') }}
              </th>
              <th
                class="px-4 py-3 text-start text-xs font-medium text-n-slate-11 uppercase"
              >
                {{ t('SOFTPHONE_SETTINGS.LIST.ENABLED') }}
              </th>
              <th
                class="px-4 py-3 text-end text-xs font-medium text-n-slate-11 uppercase"
              >
                {{ t('SOFTPHONE_SETTINGS.LIST.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-for="row in rows" :key="row.user_id">
              <td class="px-4 py-3">
                <div class="flex items-center gap-2.5 min-w-0">
                  <Avatar :name="row.name" :src="row.avatar_url" :size="28" />
                  <span class="text-sm text-n-slate-12 truncate">
                    {{ row.name }}
                  </span>
                </div>
              </td>
              <td class="px-4 py-3 text-sm tabular-nums text-n-slate-12">
                {{ row.extension || '—' }}
              </td>
              <td class="px-4 py-3">
                <div class="flex items-center gap-1.5 text-sm">
                  <span
                    class="rounded-full size-2 shrink-0"
                    :class="STATUS_DOT[statusOf(row)]"
                  />
                  <span
                    :class="
                      statusOf(row) === 'online'
                        ? 'text-n-slate-12'
                        : 'text-n-slate-11'
                    "
                  >
                    {{ t(`SOFTPHONE_SETTINGS.STATUS.${statusOf(row).toUpperCase()}`) }}
                  </span>
                  <span
                    v-if="statusOf(row) === 'online' && row.last_rtt_ms != null"
                    class="text-xs tabular-nums text-n-slate-10"
                  >
                    {{ t('SOFTPHONE_SETTINGS.STATUS.RTT', { ms: row.last_rtt_ms }) }}
                  </span>
                  <span
                    v-else-if="statusOf(row) === 'offline'"
                    class="text-xs text-n-slate-10"
                  >
                    {{ lastSeenLabel(row) }}
                  </span>
                </div>
              </td>
              <td class="px-4 py-3">
                <Switch
                  v-if="row.extension"
                  :model-value="row.enabled"
                  :disabled="busyUserId === row.user_id"
                  @change="value => toggleEnabled(row, value)"
                />
              </td>
              <td class="px-4 py-3 text-end">
                <div class="inline-flex items-center gap-2">
                  <Button
                    v-if="!row.extension"
                    size="xs"
                    :label="t('SOFTPHONE_SETTINGS.ACTIONS.ACTIVATE')"
                    :is-loading="busyUserId === row.user_id"
                    @click="activate(row)"
                  />
                  <template v-else>
                    <Button
                      size="xs"
                      faded
                      slate
                      :label="t('SOFTPHONE_SETTINGS.ACTIONS.NEW_PASSWORD')"
                      :disabled="busyUserId === row.user_id"
                      @click="askConfirm('password', row)"
                    />
                    <Button
                      size="xs"
                      faded
                      ruby
                      :label="t('SOFTPHONE_SETTINGS.ACTIONS.REMOVE')"
                      :disabled="busyUserId === row.user_id"
                      @click="askConfirm('remove', row)"
                    />
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <Dialog
        ref="confirmDialog"
        type="alert"
        :title="confirmTitle"
        :description="confirmDescription"
        :confirm-button-label="t('SOFTPHONE_SETTINGS.CONFIRM.YES')"
        :cancel-button-label="t('SOFTPHONE_SETTINGS.CONFIRM.NO')"
        @confirm="confirmPending"
      />
    </template>
  </SettingsLayout>
</template>
